<template>
  <div>
    <b-container class="wide">
      <b-row class="no-margin-left ml-3 mt-5">
        <b-button
            class="mb-4"
            variant="outline-primary"
            @click="goBack"
        >
          <b-icon icon="arrow-left" class="mr-2" />
          {{ $t('links.admin.go_back') }}
        </b-button>
      </b-row>

      <b-row class="no-margin-left ml-3 mt-3">
        <b-card-group deck>
          <b-col
            cols="auto"
            v-for="svc in shelfTypeServicesWithDiscount"
            :key="svc.id"
          >
            <b-card
              header-class="text-center font-weight-bold"
              body-class="text-center"
              class="mb-5 mr-2 booking-image-card"
            >
              <template #header>
                <div class="d-flex align-items-center justify-content-center" :class="{ 'header-margin': !svc.discountedPrice }">
                  <h5 class="mb-0 mr-2">{{ svc.service.name }}</h5>
                  <b-badge
                    v-if="svc.discountedPrice"
                    variant="danger"
                    class="d-inline-flex align-items-center"
                  >
                      <b-icon icon="award-fill" class="mr-1" />
                      {{ $t("table.shelf_types.discount_title") }}
                  </b-badge>
                </div>
                <!-- Discount validity info -->
                <small
                    v-if="svc.discountedPrice && svc.discountValidTo"
                    class="discount-info d-block mb-2"
                >
                  {{ $t("table.shelf_types.discount_active_text", { date: formatDate(svc.discountValidTo, market?.supportCountry?.code, adminMarket?.timezone) }) }}
                </small>
              </template>

              <div class="booking-image-wrapper mb-2">
                <b-card-img
                  top
                  v-if="svc.service.image"
                  :src="svc.service.image"
                  :alt="svc.service.name"
                  class="booking-image"
                />
                <div v-else class="shelf-type-placeholder">
                  <b-card-img
                    top
                    :src="require('@base/assets/images/svg/additional-service-placeholder.svg')"
                    class="booking-image"
                  />
                </div>
              </div>

              <b-card-text class="small px-2">
                {{ svc.service.description }}
              </b-card-text>

              <template #footer>
                <div class="footer-content d-flex justify-content-between align-items-center px-3 py-2">
                  <div class="float-left mt-1">
                    <span v-if="svc.discountedPrice" class="text-danger font-weight-bold h5">
                      {{ formatMoney(svc.discountedPrice, market) }}
                      <small class="text-muted">(-{{ svc.discountPercent }}%)</small>
                    </span>
                    <span v-else class="font-weight-bold h5">
                      {{ formatMoney(svc.originalPrice, market) }}
                    </span>
                    <small
                      v-if="svc.discountedPrice"
                      class="text-muted d-block"
                    >
                      <span style="text-decoration: line-through;">{{ formatMoney(svc.originalPrice, market) }}</span>
                    </small>
                  </div>
                  <div class="float-right d-flex align-items-center">
                    <b-button
                      size="sm"
                      variant="outline-secondary"
                      :disabled="quantities[svc.id] <= 0"
                      @click="decrement(svc.id)"
                    >–</b-button>
                    <span class="px-2">{{ quantities[svc.id] }}</span>
                    <b-button
                      size="sm"
                      variant="outline-secondary"
                      @click="increment(svc.id)"
                    >+</b-button>
                  </div>
                </div>
              </template>
            </b-card>
          </b-col>
        </b-card-group>
      </b-row>

      <b-row class="mt-4 ml-3 mr-3">
        <b-col>
          <div class="d-flex justify-content-between align-items-center border-top pt-3">
            <span class="font-weight-bold">{{ $t('table.additional_services.price_total') }}:</span>
            <span class="h5">{{ formatMoney(totalSelectedPrice, market) }}</span>
          </div>
        </b-col>
      </b-row>
      <b-row class="mt-3 no-margin-left ml-3 mr-3 mb-3">
        <b-col class="d-flex justify-content-end">
          <b-button
            size="sm"
            variant="outline-secondary"
            class="mr-3 skip-button"
            @click="$emit('skipStep')"
          >
            <b-icon icon="x-circle" class="mr-1" />
            {{ $t('table.additional_services.button_skip_step') }}
          </b-button>
          <b-button
            variant="success"
            :disabled="totalSelectedCount === 0"
            @click="next"
          >
            {{ $t('table.additional_services.button_next') }}
            <b-icon icon="arrow-right" class="ml-1" />
          </b-button>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import Formatting from "@base/mixins/Formatting.vue";
import DiscountUtilsMixin from "@base/mixins/DiscountUtilsMixin.vue";

export default {
  name: 'SelectAdditionalServicesComponent',
  mixins: [ Formatting, DiscountUtilsMixin ],
  data () {
    return {
      quantities: {},
    }
  },

  props: {
    shelfTypeServices: { type: Array, required: true },
    market: { type: Object, required: true },
  },
  computed: {
    totalSelectedCount() {
      return Object.values(this.quantities).reduce((sum, n) => sum + n, 0)
    },
    totalSelectedPrice() {
      return this.shelfTypeServicesWithDiscount
        .reduce((sum, item) => sum + (this.quantities[item.id] || 0) * parseFloat(item.discountedPrice || item.originalPrice), 0)
        .toFixed(2)
    },

    shelfTypeServicesWithDiscount() {
      return this.shelfTypeServices.map(p => {
        const originalPrice = parseFloat(p.price)
        const discountedPrice = this.computeDiscountedPrice(
          originalPrice,
          p.discountPercent,
          p.discountValidFrom,
          p.discountValidTo
        )

        return {
          ...p,
          originalPrice,
          discountedPrice
        }
      })
    }
  },
  methods: {
    increment(id) {
      this.$set(this.quantities, id, (this.quantities[id] || 0) + 1)
    },
    decrement(id) {
      const cur = this.quantities[id] || 0
      if (cur > 0) this.$set(this.quantities, id, cur - 1)
    },
    next() {
      const selected = Object.entries(this.quantities)
        .filter(([, qty]) => qty > 0)
        .map(([id, qty]) => ({
          service: this.shelfTypeServicesWithDiscount.find(x => x.id === +id).service.id,
          serviceName: this.shelfTypeServicesWithDiscount.find(x => x.id === +id).service.name,
          discountedPrice: this.shelfTypeServicesWithDiscount.find(x => x.id === +id).discountedPrice,
          originalPrice: this.shelfTypeServicesWithDiscount.find(x => x.id === +id).originalPrice,
          unitPrice: this.shelfTypeServicesWithDiscount.find(x => x.id === +id).discountedPrice || this.shelfTypeServicesWithDiscount.find(x => x.id === +id).originalPrice,
          isDiscountActive: this.isDiscountActive(
              this.shelfTypeServicesWithDiscount.find(x => x.id === +id).discountPercent,
              this.shelfTypeServicesWithDiscount.find(x => x.id === +id).discountValidFrom,
              this.shelfTypeServicesWithDiscount.find(x => x.id === +id).discountValidTo),
          quantity: qty,
        }))
      this.$emit('serviceSelected', selected)
    },
    goBack() {
      this.$emit('backToShelfPeriodPrices')
    }
  },
  mounted() {
    this.shelfTypeServices.forEach(svc => {
      this.$set(this.quantities, svc.id, 0)
    })
  }
}
</script>

<style scoped>
  .skip-button {
    font-weight: 500;
    color: #6c757d;
    border-color: #6c757d;
    transition: background-color .2s;
  }
  .skip-button:hover {
    background-color: rgba(108,117,125,0.1);
  }

  .card .card-header {
    background-color: transparent;
    border-color: transparent;
    padding: 0.6rem 0 0 0;
  }

  .card .card-body {
    padding: 0;
  }

  .booking-image-card {
    min-width: 20rem !important;
    min-height: 480px !important;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    transition: transform 0.2s ease;
  }

  .booking-image-card:hover {
    transform: translateY(-4px);
  }

  .booking-image:hover {
    transform: scale(1.08);
  }

  .booking-image-wrapper {
    width: 100%;
    display: flex;
    justify-content: center;
  }

  .booking-image {
    width: 100%;
    height: auto;
    object-fit: contain;
    object-position: center;
    transition: transform 0.3s ease;
  }

  .footer-content {
    min-height: 4.5rem;
  }

  .shelf-type-placeholder {
    width: 200px;
    min-height: 210px !important;
    border: 2px dashed #c4c4c4;
    border-radius: 6px;
  }

  .header-margin {
    margin-bottom: 1.75rem;
  }

  .discount-info {
    font-size: 0.85rem;
    font-weight: 500;
    color: #28a745;
  }
</style>
